import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadCloud, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Input } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ASSET_TYPE_ORDER, type AssetType } from '@/types/merchant-asset';

export interface AssetUpsertValues {
  name: string;
  type: AssetType;
  currentFileUrl: string;
}

interface AssetUpsertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AssetUpsertValues) => void;
  isPending?: boolean;
  /** Upload a file via the BFF pre-signed proxy → returns the resulting CDN URL. */
  onUpload?: (file: File, type: AssetType) => Promise<string>;
}

const HTTPS_URL = /^https:\/\/.+/;

/**
 * Create dialog (Spec 514 §7). `currentFileUrl` is required (no metadata-only
 * asset). It can be filled either by uploading a file (pre-signed S3 PUT via the
 * BFF proxy) or by entering an existing HTTPS CDN URL (both contract-valid).
 * Rename runs inline in the library row, not here.
 */
export function AssetUpsertDialog({ open, onOpenChange, onSubmit, isPending, onUpload }: AssetUpsertDialogProps) {
  const { t } = useTranslation('merchant-assets');
  const [name, setName] = useState('');
  const [type, setType] = useState<AssetType>('background');
  const [currentFileUrl, setCurrentFileUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setType('background');
      setCurrentFileUrl('');
      setUploading(false);
      setUploadError(false);
    }
  }, [open]);

  const nameValid = name.trim().length > 0 && name.length <= 200;
  const urlValid = HTTPS_URL.test(currentFileUrl.trim()) && currentFileUrl.trim().length <= 2048;
  const canSubmit = nameValid && urlValid && !uploading;

  const handleFile = async (file: File | undefined) => {
    if (!file || !onUpload) return;
    setUploadError(false);
    setUploading(true);
    try {
      const url = await onUpload(file, type);
      setCurrentFileUrl(url);
    } catch {
      setUploadError(true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), type, currentFileUrl: currentFileUrl.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{t('upsert.createTitle')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="asset-name">{t('upsert.nameLabel')}</Label>
              <Input
                id="asset-name"
                value={name}
                maxLength={200}
                placeholder={t('upsert.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-type">{t('upsert.typeLabel')}</Label>
              <Select value={type} onValueChange={(v) => setType(v as AssetType)}>
                <SelectTrigger id="asset-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPE_ORDER.map((tp) => (
                    <SelectItem key={tp} value={tp}>{t(`types.${tp}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {onUpload && (
              <div className="space-y-1.5">
                <input
                  ref={fileInputRef}
                  id="asset-file"
                  type="file"
                  className="hidden"
                  onChange={(e) => void handleFile(e.target.files?.[0])}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('upsert.uploading')}</>
                  ) : (
                    <><UploadCloud className="mr-2 h-4 w-4" /> {t('upsert.uploadLabel')}</>
                  )}
                </Button>
                {uploadError && <p className="text-xs text-destructive">{t('upsert.uploadError')}</p>}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="asset-url">{t('upsert.fileUrlLabel')}</Label>
              <Input
                id="asset-url"
                type="url"
                value={currentFileUrl}
                maxLength={2048}
                placeholder={t('upsert.fileUrlPlaceholder')}
                onChange={(e) => setCurrentFileUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t('upsert.fileUrlHelp')}</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit || isPending}>
              {t('upsert.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
